/* eslint-disable camelcase */
import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useContext,
} from "react";

import { useTranslation } from "react-i18next";

import { IdType, SortingRule } from "react-table";
import { HvButton, HvGrid } from "@hitachivantara/uikit-react-core";
import {
  HvCellProps,
  HvTableColumnConfig,
  useHvTable,
  useHvBulkActions,
  useHvPagination,
  useHvRowSelection,
  useHvSortBy,
} from "@hitachivantara/uikit-react-lab";
import { Archive, Edit } from "@hitachivantara/uikit-react-icons";

import SimpleTable, {
  numberColumn,
  textColumn,
  actionsColumn,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
} from "components/common/SimpleTable";

import {
  SubCategory,
  addSubCategory,
  editSubCategory,
  archiveSubCategory,
  useSubCategories,
} from "lib/api";

import { useSnackbar } from "lib/provider/SnackbarProvider";
import { NotificationContext } from "lib/context/NotificationContext";
import ArchiveDialog from "components/common/ArchiveDialog";

import CategoryControls from "../CategoryControls";
import { CategoryFormData, CategoryFormMode } from "../AddEditCategoryDialog";

interface SubCategoryDataRow extends Record<string, unknown> {
  pk_sub_cat_id: number;
  sub_cat_name: string;
  sub_cat_desc: string;
  version: number;
  incident_type_count: number;
  id: string;
  create_timestamp: string;
  created_by: string;
  update_timestamp: string;
  update_by: string;
}

const DEFAULT_SORT = [
  {
    id: "sub_cat_name",
    dasc: false,
  },
];

const sortFieldMap = {
  sub_cat_name: "sub_cat_name",
  sub_cat_desc: "sub_cat_desc",
  incident_type_count: "incident_type_count",
};

const translationScope = "incidentTypeSubCategory";

const SubCategoryTable: React.FC = () => {
  const { t } = useTranslation(translationScope);
  const [tableData, setTableData] = useState<SubCategory[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchKey, setSearchKey] = useState("");

  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { setShowBanner, setBannerMessage } = useContext(NotificationContext);

  const [editing, setEditing] = useState<SubCategory | "new" | "none">("none");

  const onSave = useCallback(
    (data: CategoryFormData) => {
      if (editing !== "none") {
        if (editing !== "new") {
          return editSubCategory({
            ...editing,
            sub_cat_name: data.name,
            sub_cat_desc: data.description,
          });
        }

        return addSubCategory({
          sub_cat_name: data.name,
          sub_cat_desc: data.description,
        });
      }

      return Promise.resolve();
    },
    [editing]
  );

  const addButtonCallback = useCallback(() => {
    setEditing("new");
  }, []);

  const editButtonCallback = useCallback((category: SubCategory) => {
    setEditing(category);
  }, []);

  const addEditDialogCloseCallback = () => {
    setEditing("none");
  };

  let editingMode: CategoryFormMode | undefined;
  let editingFormData;
  if (editing !== "none") {
    if (editing !== "new") {
      editingMode = "edit";
      editingFormData = {
        name: editing.sub_cat_name,
        description: editing.sub_cat_desc,
      };
    } else {
      editingMode = "add";
      editingFormData = { name: "", description: "" };
    }
  }

  const columns = useMemo(
    (): HvTableColumnConfig<SubCategory>[] => [
      textColumn({
        Header: t("columns.subCategoryName") as string,
        accessor: "sub_cat_name",
        style: { width: "20%" },
      }),
      textColumn({
        Header: t("columns.subCategoryDescription") as string,
        accessor: "sub_cat_desc",
        disableSortBy: true,
      }),
      numberColumn({
        Header: t("columns.subCategoryIncidentTypeCount") as string,
        accessor: "incident_type_count",
        style: { width: 150 },
        disableSortBy: true,
      }),
      actionsColumn({
        id: "edit",
        Cell: ({ row }: HvCellProps<SubCategory>) => {
          return (
            <HvButton
              aria-label={t("labels.editSubCategoryButton")}
              icon
              onClick={() => editButtonCallback(row.original as SubCategory)}
            >
              <Edit />
            </HvButton>
          );
        },
      }),
    ],
    [editButtonCallback, t]
  );

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);

  const [sortBy, setSortBy] =
    useState<SortingRule<SubCategoryDataRow>[]>(DEFAULT_SORT);
  const [selectedIds, setSelectedIds] = useState<
    Record<IdType<SubCategoryDataRow>, boolean>
  >({});

  const sortField = sortFieldMap[sortBy[0].id as keyof typeof sortFieldMap];

  const {
    data: subCategoriesData,
    error,
    mutate,
  } = useSubCategories(
    {
      pageNr: page + 1,
      pageSize: size,
      orderBy: sortField ? encodeURIComponent(sortField) : undefined,
      Order: sortBy[0].desc ? "desc" : "asc",
      SearchKey: searchKey,
    },
    { suspense: tableData.length === 0 }
  );

  useEffect(() => {
    if (subCategoriesData) {
      setTableData(subCategoriesData.data);
      setTotalCount(subCategoriesData.totalCount);
    }
  }, [subCategoriesData]);

  const initialState = useMemo(() => {
    const entries = tableData.map<[string, boolean]>((v) => [
      v.pk_sub_cat_id.toString(),
      v.incident_type_count > 0,
    ]);

    return {
      pageIndex: page,
      pageSize: size,
      lockedSelectionRowIds: (entries && Object.fromEntries(entries)) ?? {},
      selectedRowIds: selectedIds,
      sortBy,
      searchKey,
    };
  }, [tableData, page, size, sortBy, searchKey, selectedIds]);

  const labels = useMemo(
    () => ({
      selectRowCheckBoxAriaLabel: t("labels.selectSubCategoryCheckbox"),
    }),
    [t]
  );

  const bulkActions = useMemo(
    () => [
      {
        id: "archive",
        label: t("labels.bulkArchiveSubCategoryButton"),
        icon: <Archive />,
        onClick: () => setIsArchiveDialogOpen(true),
      },
    ],
    [t]
  );

  const instance = useHvTable<SubCategory>(
    {
      columns,
      data: tableData || [],
      getRowId: useCallback(
        (v: SubCategory): string => `${v.pk_sub_cat_id}`,
        []
      ),
      manualPagination: true,
      pageCount: Math.ceil((totalCount ?? 0) / (initialState?.pageSize ?? 1)),
      initialState,
      labels,
      aditivePageBulkSelection: true,
      subtractivePageBulkDeselection: true,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
    },
    useHvSortBy,
    useHvPagination,
    useHvRowSelection,
    useHvBulkActions
  );

  const {
    state: { pageIndex, pageSize, sortBy: sortByFromTable, selectedRowIds },
  } = instance;

  useEffect(() => setPage?.(pageIndex), [pageIndex, setPage]);
  useEffect(() => setSize?.(pageSize), [pageSize, setSize]);
  useEffect(() => {
    setSortBy?.(sortByFromTable);
    setPage?.(0);
  }, [sortByFromTable, setSortBy, setPage]);
  useEffect(() => setSelectedIds(selectedRowIds), [selectedRowIds]);

  const onEnterHandler = (value: string) => {
    setSearchKey(value);
    instance.gotoPage(DEFAULT_PAGE);
  };

  const onArchiveClose = useCallback(
    (result, message, ids) => {
      setIsArchiveDialogOpen(false);

      if (ids) {
        const newSelectedIds = {
          ...selectedIds,
        };

        ids.forEach((id: string) => {
          delete newSelectedIds[id];
        });
        setSelectedIds(newSelectedIds);
      }

      if (result && ids) {
        enqueueSnackbar(message, {
          variant: "success",
        });
        setPage(DEFAULT_PAGE);
        mutate();
      } else if (message) {
        setBannerMessage(message);
        setShowBanner(true);
        mutate();
      }
    },
    [enqueueSnackbar, mutate, selectedIds, setBannerMessage, setShowBanner]
  );

  return (
    <HvGrid container spacing={2}>
      <HvGrid item xs={12}>
        <CategoryControls
          type="subcategory"
          onAdd={addButtonCallback}
          onSave={onSave}
          onShouldRevalidate={mutate}
          onAddEditDialogClose={addEditDialogCloseCallback}
          mode={editingMode}
          editingFormData={editingFormData}
          onEnterHandler={onEnterHandler}
        />
      </HvGrid>
      <HvGrid item xs={12}>
        <ArchiveDialog
          scope={translationScope}
          isOpen={isArchiveDialogOpen}
          promise={archiveSubCategory}
          onClose={onArchiveClose}
          ids={Object.keys(selectedRowIds)}
        />
        <SimpleTable
          instance={instance}
          bulkActions={bulkActions}
          totalCount={totalCount}
          loading={(!subCategoriesData && !error) || tableData === []}
        />
      </HvGrid>
    </HvGrid>
  );
};

export default SubCategoryTable;
